import React from 'react';
import i18next from 'i18next';
import { Box, CircularProgress, Icon, IconButton, Link, Typography, Tooltip } from '@material-ui/core';
import { DateFormatter, PassedState, FileManager } from 'components/AppTable/CellsComponents';

const incompleteLabel = () => i18next.t('trainingSessions.incomplete');

const formatDateOrIncomplete = value => {
  if (!value) {
    return incompleteLabel();
  }
  return <DateFormatter value={value} />;
};

const formatResultStatus = (statusParams, isMoodleSession, helpers) => {
  if (!statusParams) {
    return '—';
  }

  if (statusParams.isFinalized) {
    const labelKey =
      statusParams.isPerformedWithSuccess === true
        ? 'students.passed'
        : statusParams.isPerformedWithSuccess === false
        ? 'students.failed'
        : null;
    if (!labelKey) {
      return incompleteLabel();
    }
    return (
      <Typography variant="body2" component="span">
        {i18next.t(labelKey)}
      </Typography>
    );
  }

  return (
    <PassedState
      id={statusParams.userId}
      status={statusParams}
      onChange={helpers.handleToggleState}
      isMoodleCourse={isMoodleSession || false}
    />
  );
};

const formatEvidence = (evidenceParams, helpers) => {
  if (!evidenceParams) {
    return incompleteLabel();
  }

  const {
    hasCertificate,
    hasAssessmentResult,
    isPerformedWithSuccess,
    certificate,
    userId,
    isMoodleCourse,
    isFrozen = false,
  } = evidenceParams;

  if (isPerformedWithSuccess === false) {
    return incompleteLabel();
  }

  if (!hasCertificate && !hasAssessmentResult && isPerformedWithSuccess == null) {
    return incompleteLabel();
  }

  const canLazyMoodleCertificate = Boolean(isMoodleCourse) && isPerformedWithSuccess === true;
  const showCertificateUpload = !isFrozen && !isMoodleCourse && isPerformedWithSuccess === true && !hasCertificate;
  const showCertificateDownload = hasCertificate || canLazyMoodleCertificate;
  const showQuizAnswers = Boolean(hasAssessmentResult);
  const isCertificateDownloading = helpers.isDownloadingCertificate?.(userId);
  const isAssessmentDownloading = helpers.isDownloadingAssessment?.(userId);

  if (!showCertificateUpload && !showCertificateDownload && !showQuizAnswers) {
    return incompleteLabel();
  }

  return (
    <Box display="flex" alignItems="center" style={{ gap: 4 }}>
      {showCertificateDownload || showCertificateUpload ? (
        <FileManager
          value={{
            id: userId,
            certificate: hasCertificate ? certificate || {} : canLazyMoodleCertificate ? {} : null,
            isPerformedWithSuccess,
            isMoodleCourse,
            isFrozen,
            isDownloading: Boolean(isCertificateDownloading),
          }}
          onChange={helpers.handleAddCertificate}
          onShowCertificate={helpers.handleShowCertificate}
        />
      ) : null}
      {showQuizAnswers ? (
        isAssessmentDownloading ? (
          <CircularProgress size={18} />
        ) : (
          <Tooltip title={i18next.t('trainingSessions.answersDownloadTooltip')} placement="top" arrow>
            <IconButton size="small" color="primary" onClick={() => helpers.handleDownloadQuizAnswers(userId)}>
              <Icon fontSize="small">get_app</Icon>
            </IconButton>
          </Tooltip>
        )
      ) : null}
    </Box>
  );
};

export const getParticipantsTableConfig = (
  helpers,
  { showAttemptsColumn = false, forTrainer = false } = {},
) => {
  const columns = [
    {
      title: i18next.t('tablesLabels.studentId'),
      field: 'ids',
      columnWidth: '85px',
    },
    {
      title: i18next.t('tablesLabels.name'),
      field: 'fullName',
      columnWidth: '200px',
    },
  ];

  if (!forTrainer) {
    columns.push({
      title: i18next.t('tablesLabels.email'),
      field: 'email',
      columnWidth: '200px',
    });
  }

  columns.push(
    {
      title: i18next.t('trainingSessions.attemptCompletionDate'),
      field: 'completionDate',
      columnWidth: '140px',
      Component: formatDateOrIncomplete,
    },
    {
      title: i18next.t('tablesLabels.expiryDate'),
      field: 'expiryDate',
      columnWidth: '140px',
      Component: formatDateOrIncomplete,
    },
    {
      title: i18next.t('tablesLabels.status'),
      field: 'statusParams',
      dependentField: 'isMoodleSession',
      columnWidth: '160px',
      Component: (statusParams, isMoodleSession) => formatResultStatus(statusParams, isMoodleSession, helpers),
    },
    {
      title: i18next.t('trainingSessions.evidence'),
      field: 'evidenceParams',
      columnWidth: '160px',
      Component: value => formatEvidence(value, helpers),
    },
  );

  if (showAttemptsColumn) {
    columns.push({
      title: i18next.t('trainingSessions.attempts'),
      field: 'attemptParams',
      columnWidth: '110px',
      Component: attemptParams => {
        if (!attemptParams?.showAttempts) {
          return '—';
        }
        const count = attemptParams?.count || 0;
        return (
          <Link component="button" onClick={() => helpers.handleOpenAttempts(attemptParams.userId)}>
            {i18next.t('trainingSessions.attemptsCount', { count })}
          </Link>
        );
      },
    });
  }

  columns.push({
    title: '',
    field: 'id',
    isVisible: false,
  });

  return columns;
};
